import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { File, FileText, Folder } from 'lucide-react';
import { Badge, Button } from '../ui';
import CodeBlock from '../common/CodeBlock';
import { apiClient } from '../../api/client';
import { useSession } from '../../context/SessionContext';
import { classifyError } from './errors';
import PanelState from './PanelState';
import { directorySelection, fileSelection } from './selection';

const MAX_CHUNKS = 5;

/** A JSON payload rendered readably, without pretending it is prose. */
function rawText(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

/**
 * Quellenvorschau und Codeansicht (spec §9.5, §10.5).
 *
 * One surface for all four previewable selections — file, directory, symbol
 * and document — so the user never has to know which backend a source came
 * from. Files and directories come from the session file API, symbols from the
 * CKB search payload the rail already holds, documents from the Axiom chunk
 * search.
 *
 * @param {object} props
 * @param {import('./selection').KnowledgeSelection|null} props.selection
 * @param {(selection: import('./selection').KnowledgeSelection) => void} props.onSelect -
 *   used for drill-down out of a directory listing.
 * @param {() => void} [props.onShowArchitecture]
 */
export function SourcePreview({ selection, onSelect, onShowArchitecture }) {
  const { t } = useTranslation();
  const { sessionId, isConnected } = useSession();

  const [content, setContent] = useState(null);
  const [entries, setEntries] = useState(null);
  const [chunks, setChunks] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const kind = selection?.kind || null;
  const path = selection?.path || null;
  const documentId = kind === 'document' ? selection.id : null;
  const documentName = kind === 'document' ? selection.name : null;
  const documentSource = kind === 'document' ? selection.source : null;

  const load = useCallback(() => {
    if (!selection) return undefined;
    let cancelled = false;

    // A repeat load with content already on screen is a partial refresh, not a
    // cold load — the spec's "streaming/partial" state (§11).
    const hadContent = content !== null || entries !== null || chunks !== null;
    if (hadContent) setRefreshing(true);
    else setLoading(true);
    setError(null);

    const finish = () => {
      if (cancelled) return;
      setLoading(false);
      setRefreshing(false);
    };

    if (kind === 'file' || kind === 'directory') {
      if (!sessionId) {
        setError(new Error('No active session'));
        finish();
        return undefined;
      }
      apiClient
        .getFileContent(sessionId, path)
        .then((result) => {
          if (cancelled) return;
          if (result.type === 'directory') {
            setEntries(result.tree || []);
            setContent(null);
          } else {
            setContent(result.content ?? '');
            setEntries(null);
          }
        })
        .catch((err) => {
          if (!cancelled) setError(err);
        })
        .finally(finish);
      return () => {
        cancelled = true;
      };
    }

    if (kind === 'document') {
      // Catalog imports have no chunk API — their preview is the metadata card.
      if (documentSource === 'catalog') {
        setChunks([]);
        finish();
        return undefined;
      }
      apiClient
        .request('/axiom/search', {
          method: 'POST',
          body: JSON.stringify({ query: documentName, limit: MAX_CHUNKS })
        })
        .then((result) => {
          if (cancelled) return;
          const all = result?.results || [];
          const own = all.filter(
            (chunk) => String(chunk.documentId ?? chunk.document_id ?? '') === String(documentId)
              || chunk.filename === documentName
          );
          setChunks((own.length > 0 ? own : all).slice(0, MAX_CHUNKS));
        })
        .catch((err) => {
          if (!cancelled) setError(err);
        })
        .finally(finish);
      return () => {
        cancelled = true;
      };
    }

    finish();
    return undefined;
    // `content`/`entries`/`chunks` are read only to decide cold vs. partial and
    // must not retrigger the fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection, kind, path, sessionId, documentId, documentName, documentSource]);

  useEffect(() => {
    setContent(null);
    setEntries(null);
    setChunks(null);
  }, [selection?.id, selection?.kind]);

  useEffect(() => load(), [load]);

  const header = useMemo(() => {
    if (!selection) return null;
    return (
      <div className="etch-knowledge__preview-header">
        <p className="etch-knowledge__preview-path" title={selection.path || selection.name}>
          {selection.path || selection.name}
        </p>
        <div className="etch-knowledge__preview-badges">
          <Badge>{t(`knowledge.kind.${selection.kind}`, { defaultValue: selection.kind })}</Badge>
          {selection.symbolKind && <Badge>{selection.symbolKind}</Badge>}
          {selection.authority && <Badge tone="brand">{selection.authority}</Badge>}
          {refreshing && (
            <span className="etch-knowledge__preview-refreshing" role="status" aria-live="polite">
              {t('knowledge.refreshing')}
            </span>
          )}
        </div>
      </div>
    );
  }, [selection, refreshing, t]);

  if (!selection) {
    return (
      <PanelState
        status="empty"
        title={t('knowledge.noSelection')}
        description={t('ide.noFileHint')}
        action={
          <>
            {/* `Button` always renders a real <button>; a route change needs an
                anchor, so the link borrows the button skin instead. */}
            <Link to="/onboarding" className="etch-button" data-variant="secondary" data-size="compact">
              {t('ide.startOnboarding')}
            </Link>
            {onShowArchitecture && (
              <Button variant="ghost" size="compact" onClick={onShowArchitecture}>
                {t('ide.viewArchitecture')}
              </Button>
            )}
          </>
        }
      />
    );
  }

  if (loading) return <PanelState status="loading" />;

  if (error) {
    const status = classifyError(error);
    return (
      <PanelState
        status={status}
        error={error}
        description={status === 'session' && !isConnected ? t('ide.connectPrompt') : undefined}
        onRetry={load}
      />
    );
  }

  if (kind === 'directory' || (kind === 'file' && entries)) {
    return (
      <div className="etch-knowledge__preview">
        {header}
        {entries?.length ? (
          <ul className="etch-knowledge__dir-list" aria-label={t('knowledge.directoryContents')}>
            {entries.map((entry) => (
              <li key={entry.path}>
                <button
                  type="button"
                  className="etch-knowledge__dir-item"
                  onClick={() =>
                    onSelect?.(
                      entry.type === 'directory'
                        ? directorySelection({ path: entry.path, name: entry.name })
                        : fileSelection({ path: entry.path, name: entry.name })
                    )
                  }
                >
                  <span className="etch-knowledge__dir-icon" aria-hidden="true">
                    {entry.type === 'directory' ? <Folder size={15} /> : <File size={15} />}
                  </span>
                  <span>{entry.name}</span>
                  <span className="etch-visually-hidden">
                    {t(entry.type === 'directory' ? 'knowledge.kind.directory' : 'knowledge.kind.file')}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <PanelState status="empty" title={t('knowledge.emptyDirectory')} />
        )}
      </div>
    );
  }

  if (kind === 'file') {
    if (content === null) return <PanelState status="loading" />;
    if (content === '') {
      return (
        <div className="etch-knowledge__preview">
          {header}
          <PanelState status="empty" title={t('knowledge.emptyFile')} />
        </div>
      );
    }
    return (
      <div className="etch-knowledge__preview">
        {header}
        <div className="etch-knowledge__preview-body" data-variant="code">
          <CodeBlock code={content} filename={selection.name} />
        </div>
      </div>
    );
  }

  if (kind === 'symbol' || kind === 'node') {
    const detail = selection.data || {};
    const rows = [
      [t('knowledge.column.name'), selection.name],
      [t('knowledge.column.kind'), selection.symbolKind],
      [t('knowledge.column.file'), selection.file || selection.path],
      [t('knowledge.column.layer'), detail.boundary?.layer || detail.role],
      [t('knowledge.column.loc'), detail.metrics?.loc ?? detail.loc],
      [t('knowledge.column.symbols'), detail.symbolCount],
      [t('knowledge.column.files'), detail.fileCount]
    ].filter(([, value]) => value !== undefined && value !== null && value !== '');

    return (
      <div className="etch-knowledge__preview">
        {header}
        <dl className="etch-knowledge__meta">
          {rows.map(([label, value]) => (
            <div className="etch-knowledge__meta-row" key={label}>
              <dt className="etch-knowledge__meta-key">{label}</dt>
              <dd className="etch-knowledge__meta-val">{String(value)}</dd>
            </div>
          ))}
        </dl>
        {(selection.file || selection.path) && (
          <Button
            variant="secondary"
            size="compact"
            onClick={() => {
              const target = selection.file || selection.path;
              onSelect?.(fileSelection({ path: target, name: target.split('/').pop() }));
            }}
          >
            {t('knowledge.openFile')}
          </Button>
        )}
        <div className="etch-knowledge__preview-body" data-variant="raw">
          <pre>{rawText(detail)}</pre>
        </div>
      </div>
    );
  }

  if (kind === 'document') {
    const rows = [
      [t('knowledge.column.source'), t(selection.source === 'catalog' ? 'knowledge.sourceCatalog' : 'knowledge.sourceAxiom')],
      [t('knowledge.column.authority'), selection.authority],
      [t('knowledge.column.status'), selection.status],
      [t('knowledge.column.chunks'), selection.chunkCount]
    ].filter(([, value]) => value !== undefined && value !== null && value !== '');

    return (
      <div className="etch-knowledge__preview">
        {header}
        <dl className="etch-knowledge__meta">
          {rows.map(([label, value]) => (
            <div className="etch-knowledge__meta-row" key={label}>
              <dt className="etch-knowledge__meta-key">{label}</dt>
              <dd className="etch-knowledge__meta-val">{String(value)}</dd>
            </div>
          ))}
        </dl>

        {chunks === null ? (
          <PanelState status="loading" compact />
        ) : chunks.length === 0 ? (
          <PanelState
            status="empty"
            title={t('knowledge.noPreviewAvailable')}
            description={t('knowledge.noPreviewHint')}
            compact
          />
        ) : (
          <ul className="etch-knowledge__chunks" aria-label={t('knowledge.preview')}>
            {chunks.map((chunk, index) => (
              <li key={chunk.chunkId || index} className="etch-knowledge__chunk">
                <p className="etch-knowledge__chunk-head">
                  <FileText size={14} aria-hidden="true" />
                  <span>{chunk.filename || selection.name}</span>
                  {chunk.authority && <Badge tone="brand">{chunk.authority}</Badge>}
                </p>
                <p className="etch-knowledge__chunk-body">{chunk.content}</p>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  return <PanelState status="empty" title={t('knowledge.noPreviewAvailable')} />;
}

export default SourcePreview;
